// src/pages/simple-instrument-form/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../utils/supabase";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import {
  buildDuplicateInstrumentMessage,
  findInstrumentDuplicates,
} from "../../utils/instrumentDuplicateGuard";
import { INSTRUMENT_TYPE_OPTIONS } from "../../utils/instrument-types";
import { useLabPermissions } from "../../hooks/useLabPermissions";

// Autocomplete por cliente (serie/tag)
import SerialTagAutocomplete from "../ingresos/components/SerialTagAutocomplete";
import InstrumentCategoriesModal from "../ingresos/components/InstrumentCategoriesModal";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// -------- utils num/strings ----------
const num = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
const intOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
};
const nullIfEmpty = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

function shouldRetryWithoutCalibrationMeta(error) {
  const msg = String(error?.message || "").toLowerCase();
  return (
    msg.includes("reception_date") ||
    msg.includes("calibration_place") ||
    msg.includes("departure_date")
  );
}

function todayIsoDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildEmptyFormData(clientId = "") {
  return {
    client_id: clientId,
    type: "pressure",
    category_id: "",
    tag: "",
    brand: "",
    model: "",
    serial: "",
    unit: "",
    range_min: "",
    range_max: "",
    resolution: "",
    tolerance: "",
    is_analog: false,
    calibrationintervalmonths: "",
    status: "active",
    observations: "",
    baker_code: "",
    reception_date: todayIsoDate(),
    departure_date: "",
    calibration_place: "Laboratorio",
  };
}
const autoTag = (type) => {
  const pref =
    {
      pressure: "MANO",
      temperature: "TERM",
      balance: "BAL",
      dimensional: "CAL",
      electrical: "DMM",
      mass: "BAL",
      flow: "CAUD",
      force: "TORQ",
      gas: "GAS",
      sound: "DB",
      illuminance: "LUX",
      air_velocity: "ANE",
      analytic: "LAB",
    }[type] || "ID";
  return `${pref}-${Math.floor(1000 + Math.random() * 9000)}`;
};

// ----- helpers QR / archivos -----
function dataUrlToBlob(dataUrl) {
  const [meta, base64] = dataUrl.split(",");
  const mimeMatch = /data:(.*);base64/.exec(meta);
  const mime = mimeMatch?.[1] || "image/png";
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: mime });
}

async function buildMiniPdfBlob(value) {
  const png = await QRCode.toDataURL(String(value), {
    errorCorrectionLevel: "Q",
    scale: 8,
    margin: 0,
  });
  const doc = new jsPDF({ unit: "mm", format: [12, 12] });
  doc.addImage(png, "PNG", 0.5, 0.5, 11, 11);
  return doc.output("blob");
}

/**
 * Genera y guarda automáticamente:
 *  - PNG del QR (labels/qr/<uuid>.png)  -> instruments.qr_image_url
 *  - PDF mini 12x12mm (labels/qr-pdf/<uuid>.pdf) -> instruments.qr_pdf_url
 * Contenido del QR: qr_scan_value (lab_id como string).
 */
async function generateAndStoreInstrumentQR({ id, qr_scan_value }) {
  if (!id || !qr_scan_value) throw new Error("Faltan id o qr_scan_value");

  const bucket = "labels";

  // 1) PNG
  const pngDataUrl = await QRCode.toDataURL(qr_scan_value, {
    errorCorrectionLevel: "Q",
    scale: 8,
    margin: 0,
  });
  const pngBlob = dataUrlToBlob(pngDataUrl);
  const pngPath = `qr/${id}.png`;
  const { error: upErrPng } = await supabase.storage
    .from(bucket)
    .upload(pngPath, pngBlob, { upsert: true, contentType: "image/png" });
  if (upErrPng) throw upErrPng;
  const { data: pngPub } = supabase.storage.from(bucket).getPublicUrl(pngPath);
  const pngUrl = pngPub?.publicUrl;

  // 2) PDF mini
  const pdfBlob = await buildMiniPdfBlob(qr_scan_value);
  const pdfPath = `qr-pdf/${id}.pdf`;
  const { error: upErrPdf } = await supabase.storage
    .from(bucket)
    .upload(pdfPath, pdfBlob, {
      upsert: true,
      contentType: "application/pdf",
    });
  if (upErrPdf) throw upErrPdf;
  const { data: pdfPub } = supabase.storage.from(bucket).getPublicUrl(pdfPath);
  const pdfUrl = pdfPub?.publicUrl;

  // 3) Persistir URLs en la BD
  const { data: authData } = await supabase.auth.getUser();
  const { error: updErr } = await supabase
    .from("instruments")
    .update({
      qr_image_url: pngUrl,
      qr_pdf_url: pdfUrl,
      qr_generated_at: new Date().toISOString(),
      qr_generated_by: authData?.user?.id || null,
    })
    .eq("id", id);
  if (updErr) throw updErr;

  return { pngUrl, pdfUrl };
}

/**
 * Variables (magnitudes) permitidas en instruments.type.
 * Deben coincidir con el CHECK de la tabla instruments.
 */
const VARIABLE_OPTIONS = INSTRUMENT_TYPE_OPTIONS;

/**
 * Mapa de "variable" → nombres de categorías recomendadas.
 * Los nombres deben coincidir con instrument_categories.name.
 */
const CATEGORY_BY_TYPE = {
  pressure: [
    "Manómetro",
    "Manómetro analógico",
    "Manómetro digital",
    "Transmisor de presión",
    "Presostato",
    "Vacuómetro",
    "Válvula de seguridad",
  ],
  temperature: [
    "Termómetro de vidrio",
    "Termómetro de mercurio",
    "Termómetro de alcohol",
    "Termómetro infrarrojo",
    "RTD",
    "PT100",
    "Termocupla",
    "Termopar",
    "Estufa de cultivo",
    "Cámara climática",
  ],
  flow: [
    "Caudalímetro electromagnético",
    "Caudalímetro másico Coriolis",
    "Caudalímetro ultrasónico",
    "Caudalímetro mecánico (totalizador)",
    "Totalizador de caudal",
    "Orificio de restricción",
  ],
  dimensional: [
    "Pie de rey",
    "Calibre",
    "Micrómetro",
    "Micrómetro interior",
    "Medidor de espesores",
    "Medidor de espesores de pintura",
    "Calibre de profundidad",
    "Calibre de soldadura",
    "Comparador",
    "Peine de roscas",
    "Tamiz",
    "Probeta patrón",
    "Nivel óptico",
    "Nivel digital",
    "Nivel láser",
    "Teodolito",
  ],
  mass: ["Balanza", "Balanza analítica", "Pesas patrón"],
  electrical: [
    "Multímetro",
    "Amperímetro",
    "Ohmímetro",
    "Pinza amperométrica",
    "Calibrador de procesos",
    "Megóhmetro",
    "Telurímetro",
    "Detector de tensión",
    "Probador de disyuntores",
    "Generador de alta tensión",
    "VLF",
    "Monitor de impedancia fijo",
    "Monitor de impedancia portátil",
    "Escalera dieléctrica",
    "Guantes dieléctricos",
    "Mantas dieléctricas",
    "Taburete dieléctrico",
    "Pértiga",
  ],
  force: ["Dinamómetro", "Tensiómetro", "Torquímetro", "Célula de carga"],
  gas: ["Detector multigás", "Detector monogás", "Detector de gas fijo"],
  sound: ["Decibelímetro", "Patrón decibelímetro"],
  illuminance: ["Luxómetro"],
  air_velocity: ["Anemómetro"],
  analytic: [
    "pH-metro",
    "Oxímetro",
    "Conductivímetro",
    "Turbidímetro",
    "Refractómetro",
    "Densímetro",
    "Alcoholímetro",
  ],
};

/**
 * Unidades sugeridas por variable (para autocompletar).
 */
const UNITS_BY_TYPE = {
  pressure: ["bar", "psi", "kPa", "kg/cm²"],
  temperature: ["°C", "°F", "K"],
  electrical: ["V", "mV", "A", "mA", "Ω", "kΩ", "MΩ", "Hz"],
  dimensional: ["mm", "cm", "m", "inch", "µm"],
  balance: ["kg", "g", "mg"],
  mass: ["kg", "g", "mg", "t"],
  flow: ["m³/h", "L/min", "L/h"],
  force: ["N", "kN", "kgf", "lbf", "Nm"],
  gas: ["ppm", "%LEL", "%vol"],
  sound: ["dB", "dB(A)"],
  illuminance: ["lx"],
  air_velocity: ["m/s", "km/h"],
  analytic: ["pH", "mS/cm", "µS/cm", "NTU", "°Brix"],
  other: [],
};

export default function SimpleInstrumentForm() {
  const navigate = useNavigate();
  const query = useQuery();
  const poId = query.get("poId");
  const { isLabManager } = useLabPermissions();

  // Datos base
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [poContext, setPoContext] = useState(null);
  const [nextOsCode, setNextOsCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [receptionDateSupported, setReceptionDateSupported] = useState(null);
  const [showTechnicalData, setShowTechnicalData] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  // Form state (alineado a Intake: incluye baker_code + observations)
  const [formData, setFormData] = useState(buildEmptyFormData(""));

  // Próximo número de instrumento (SERV) dentro de la OS.
  // Espeja al trigger gen_service_order_code: OS-<base>-<max(base_seq)+1>.
  // Sirve al administrativo para saber, si retoma la carga, cuál es el siguiente.
  const loadNextOsCode = async (poIdArg, baseNumberArg) => {
    if (!poIdArg) {
      setNextOsCode(null);
      return;
    }
    try {
      const { data, error } = await supabase
        .from("service_orders")
        .select("base_number, base_seq")
        .eq("po_id", poIdArg)
        .order("base_seq", { ascending: false })
        .limit(1);
      if (error) throw error;
      const last = Array.isArray(data) && data[0] ? data[0] : null;
      const base = last?.base_number ?? baseNumberArg ?? null;
      const nextSeq = (last?.base_seq ?? 0) + 1;
      if (base == null) {
        setNextOsCode(null);
        return;
      }
      setNextOsCode(`OS-${base}-${String(nextSeq).padStart(3, "0")}`);
    } catch (e) {
      console.warn("[loadNextOsCode]", e?.message || e);
      setNextOsCode(null);
    }
  };

  // Cargar clientes, categorías y (si corresponde) la OC
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        // Clientes
        const { data: clis, error: e1 } = await supabase
          .from("clients")
          .select("id, name")
          .order("name", { ascending: true });
        if (e1) throw e1;
        if (!cancel) setClients(clis || []);

        // Categorías
        const { data: cats, error: eCats } = await supabase
          .from("instrument_categories")
          .select("id, name, variable_types")
          .order("name");
        if (eCats) throw eCats;
        if (!cancel) setCategories(cats || []);

        // Si llega poId, traemos contexto y fijamos cliente
        if (poId) {
          const { data: po, error: e2 } = await supabase
            .from("purchase_orders")
            .select(
              `
              id, code, base_number,
              client:clients ( id, name ),
              offer:offers ( id, code )
            `
            )
            .eq("id", poId)
            .single();
          if (e2) throw e2;
          if (!po?.client?.id)
            throw new Error("La OC no tiene un cliente asociado.");

          if (!cancel) {
            setPoContext({
              id: po.id,
              code: po.code,
              base_number: po.base_number,
              client: po.client,
              offer: po.offer,
            });
            setFormData((p) => ({
              ...p,
              client_id: po.client.id,
            }));
            // Próximo número de instrumento (SERV) dentro de la OS
            await loadNextOsCode(po.id, po.base_number);
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancel)
          setErrorMsg(err.message || "No se pudieron cargar datos iniciales.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [poId]);

  useEffect(() => {
    let active = true;

    async function refreshCategories() {
      const { data, error } = await supabase
        .from("instrument_categories")
        .select("id, name, variable_types")
        .order("name");
      if (!active) return;
      if (error) {
        console.error("[SimpleInstrumentForm] refresh categories", error);
        return;
      }
      setCategories(data || []);
    }

    window.addEventListener("instrument-categories-changed", refreshCategories);
    return () => {
      active = false;
      window.removeEventListener(
        "instrument-categories-changed",
        refreshCategories
      );
    };
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { error } = await supabase
          .from("instruments")
          .select("reception_date, calibration_place, departure_date")
          .limit(1);
        if (!cancel) setReceptionDateSupported(!error);
      } catch {
        if (!cancel) setReceptionDateSupported(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const lockClient = !!poContext;
  const resetForm = () => {
    setFormData(buildEmptyFormData(poContext?.client?.id || ""));
    setShowTechnicalData(false);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    });
  };

  // Categorías ordenadas por variable (recomienda primero)
  const categoriesForType = useMemo(
    () =>
      categories.filter((category) => {
        const allowed = category.variable_types || [];
        return (
          allowed.length === 0 ||
          allowed.includes(formData.type) ||
          category.id === formData.category_id
        );
      }),
    [categories, formData.type, formData.category_id]
  );

  // Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onTypeChange = (e) => {
    const nextType = e.target.value;
    const unitSuggestions = UNITS_BY_TYPE[nextType] || [];
    const defaultUnit =
      unitSuggestions.length > 0 ? unitSuggestions[0] : formData.unit || "";
    const selectedCategory = categories.find(
      (category) => category.id === formData.category_id
    );
    const allowed = selectedCategory?.variable_types || [];

    setFormData((prev) => ({
      ...prev,
      type: nextType,
      unit: defaultUnit,
      category_id:
        allowed.length > 0 && !allowed.includes(nextType)
          ? ""
          : prev.category_id,
    }));
  };

  const onCategoryChange = (e) => {
    const nextCategoryId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      category_id: nextCategoryId,
    }));
  };

  const handleCategoryCreated = (category) => {
    if (!category?.id) return;

    setCategories((current) =>
      [...current.filter((item) => item.id !== category.id), category].sort(
        (a, b) => (a.name || "").localeCompare(b.name || "", "es")
      )
    );
    setFormData((prev) => ({ ...prev, category_id: category.id }));
  };

  const tagPlaceholder = useMemo(() => {
    switch (formData.type) {
      case "dimensional":
        return "Ej: CAL-0001";
      case "electrical":
        return "Ej: DMM-0001";
      case "pressure":
        return "Ej: MANO-0001";
      case "temperature":
        return "Ej: TERM-0001";
      case "balance":
      case "mass":
        return "Ej: BAL-0001";
      case "flow":
        return "Ej: CAUD-0001";
      case "force":
        return "Ej: TORQ-0001";
      case "gas":
        return "Ej: GAS-0001";
      case "sound":
        return "Ej: DB-0001";
      case "illuminance":
        return "Ej: LUX-0001";
      case "air_velocity":
        return "Ej: ANE-0001";
      case "analytic":
        return "Ej: LAB-0001";
      default:
        return "Ej: ID-0001";
    }
  }, [formData.type]);

  const handleSubmit = async (e, { addAnother = false } = {}) => {
    e?.preventDefault?.();
    setErrorMsg("");

    if (!formData.client_id) return setErrorMsg("Seleccioná un cliente.");
    if (!formData.type) return setErrorMsg("Seleccioná la variable.");
    if (!formData.category_id)
      return setErrorMsg("Seleccioná el tipo de instrumento.");

    // Validación como Intake: rango min no puede ser mayor que max
    const rMin = num(formData.range_min);
    const rMax = num(formData.range_max);
    if (rMin !== null && rMax !== null && rMin > rMax) {
      setErrorMsg("El Rango mín. no puede ser mayor que el Rango máx.");
      return;
    }

    const finalTag = (formData.tag || autoTag(formData.type)).trim();

    const payload = {
      client_id: formData.client_id,
      type: formData.type,
      category_id: formData.category_id || null,
      subcategory_id: null,
      tag: finalTag,
      brand: nullIfEmpty(formData.brand),
      model: nullIfEmpty(formData.model),
      serial: nullIfEmpty(formData.serial),
      unit: nullIfEmpty(formData.unit),
      range_min: rMin,
      range_max: rMax,
      resolution: num(formData.resolution),
      tolerance: num(formData.tolerance),
      is_analog: !!formData.is_analog,
      calibrationintervalmonths: intOrNull(formData.calibrationintervalmonths),
      status: formData.status || "active",
      observations: nullIfEmpty(formData.observations),
      baker_code: nullIfEmpty(formData.baker_code), // como Intake
    };

    payload.reception_date = formData.reception_date || todayIsoDate();
    payload.departure_date = nullIfEmpty(formData.departure_date);
    payload.calibration_place = formData.calibration_place || "Laboratorio";

    setSaving(true);

    let instrument = null;

    try {
      const duplicates = await findInstrumentDuplicates(supabase, payload);
      if (duplicates.length > 0) {
        window.alert(buildDuplicateInstrumentMessage(duplicates, payload));
        instrument = duplicates[0];
      } else {
        // 1) Crear instrumento
        let insertResponse = await supabase
          .from("instruments")
          .insert(payload)
          .select("id, qr_scan_value")
          .single();

        if (insertResponse.error && shouldRetryWithoutCalibrationMeta(insertResponse.error)) {
          const retryPayload = { ...payload };
          delete retryPayload.reception_date;
          delete retryPayload.departure_date;
          delete retryPayload.calibration_place;
          insertResponse = await supabase
            .from("instruments")
            .insert(retryPayload)
            .select("id, qr_scan_value")
            .single();
        }

        const { data, error } = insertResponse;

        if (error) {
          console.error(error);
          setSaving(false);
          setErrorMsg(error.message || "No se pudo crear el instrumento");
          return;
        }

        instrument = data;

        // 2) Generar QR (no bloquea el flujo si falla)
        try {
          await generateAndStoreInstrumentQR(instrument);
        } catch (qrErr) {
          console.error("[generateAndStoreInstrumentQR]", qrErr);
        }
      }
    } catch (duplicateErr) {
      console.error("[findInstrumentDuplicates]", duplicateErr);
      setSaving(false);
      setErrorMsg(
        duplicateErr.message ||
          "No se pudo verificar si el instrumento ya existe. No se creó un nuevo ID."
      );
      return;
    }

    // 3) Vincular a PO / crear OS directo (según contexto)
    try {
      if (instrument?.id) {
        if (poId) {
          const { error: linkErr } = await supabase.rpc("link_instrument_to_po", {
            p_po_id: poId,
            p_instrument_id: instrument.id,
          });
          if (linkErr) throw linkErr;

          // Como el flujo real: re-secuenciar códigos OS
          const { error: seqErr } = await supabase.rpc(
            "resequence_os_codes_for_po",
            { p_po_id: poId }
          );
          if (seqErr) console.warn("resequence_os_codes_for_po:", seqErr.message);
        } else {
          // Si entra "suelto", crea OS directo (mantengo compatibilidad)
          const { error: osErr } = await supabase.rpc(
            "create_service_order_direct",
            {
              p_client_id: formData.client_id,
              p_instrument_id: instrument.id,
              p_offer_id: null,
            }
          );
          if (osErr) console.error("create_service_order_direct:", osErr);
        }
      }
    } catch (rpcErr) {
      console.error("[RPC link/create OS]", rpcErr);
      // No frenamos la navegación si el instrumento ya se creó
    } finally {
      setSaving(false);
    }

    // 3.b) Refrescar el próximo SERV para el header (si se sigue cargando)
    if (poId) {
      await loadNextOsCode(poId, poContext?.base_number);
    }

    // 4) Volver a ingresos (con focus si venía desde una OC)
    if (instrument?.id) {
      if (addAnother) {
        resetForm();
      } else if (poId) {
        navigate(`/ingresos?focus=${poId}`);
      } else {
        navigate("/ingresos");
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-muted-foreground">Cargando…</div>
      </div>
    );
  }

  const currentClientId = poContext?.client?.id || formData.client_id || null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-semibold mb-1">Registro de Instrumento</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Completá los datos del instrumento a ingresar.
      </p>

      {poContext && (
        <div className="mb-6 rounded-lg border border-amber-600/40 bg-amber-600/10 p-3 text-sm">
          <div>
            <span className="font-medium">OC:</span>{" "}
            {poContext.code || poContext.id}
          </div>
          <div>
            <span className="font-medium">Oferta:</span>{" "}
            {poContext.offer?.code || "—"}
          </div>
          <div>
            <span className="font-medium">Cliente:</span>{" "}
            {poContext.client?.name || "—"}
          </div>
          <div className="mt-2 pt-2 border-t border-amber-600/30 flex items-center gap-2">
            <span className="font-medium">Próximo instrumento (SERV):</span>
            <span className="font-mono font-semibold text-base text-amber-100">
              {nextOsCode || "…"}
            </span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-7">
        {/* 1. Identificación */}
        <section>
          <h2 className="text-lg font-medium mb-3">1. Identificación</h2>

          <div className="grid grid-cols-12 gap-4">
            {/* Cliente */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">
                Cliente <span className="text-rose-400">*</span>
              </label>

              {lockClient ? (
                <>
                  <input
                    type="text"
                    readOnly
                    value={poContext.client.name || ""}
                    className="w-full rounded-md border border-border bg-muted/20 px-3 py-2 text-slate-300"
                  />
                  <input
                    type="hidden"
                    name="client_id"
                    value={formData.client_id}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    El cliente proviene de la Orden de Compra y no puede cambiarse.
                  </p>
                </>
              ) : (
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                >
                  <option value="">Seleccionar cliente</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Variable */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">
                Variable (magnitud) <span className="text-rose-400">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={onTypeChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              >
                {VARIABLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                La variable se elige manualmente y determina qué tipos se
                muestran.
              </p>
            </div>

            {/* Autocomplete Serie / Tag */}
            <div className="col-span-12">
              <label className="block text-sm mb-1">
                Buscar por Serie / Tag (existente)
              </label>
              <SerialTagAutocomplete
                clientId={currentClientId}
                onPick={(inst) => {
                  setFormData((prev) => ({
                    ...prev,
                    tag: inst.tag || prev.tag,
                    serial: inst.serial || prev.serial,
                    brand: inst.brand || prev.brand,
                    model: inst.model || prev.model,
                    type: inst.type || prev.type,
                    category_id: inst.category_id || prev.category_id,
                    unit: inst.unit || prev.unit,
                    range_min:
                      inst.range_min != null ? String(inst.range_min) : prev.range_min,
                    range_max:
                      inst.range_max != null ? String(inst.range_max) : prev.range_max,
                    resolution:
                      inst.resolution != null ? String(inst.resolution) : prev.resolution,
                    tolerance:
                      inst.tolerance != null ? String(inst.tolerance) : prev.tolerance,
                    is_analog: typeof inst.is_analog === "boolean" ? inst.is_analog : prev.is_analog,
                    calibrationintervalmonths:
                      inst.calibrationintervalmonths != null
                        ? String(inst.calibrationintervalmonths)
                        : prev.calibrationintervalmonths,
                    observations: inst.observations || prev.observations,
                    baker_code: inst.baker_code || prev.baker_code,
                    reception_date:
                      inst.reception_date || prev.reception_date || todayIsoDate(),
                    departure_date:
                      inst.departure_date || prev.departure_date || "",
                    calibration_place:
                      inst.calibration_place ||
                      prev.calibration_place ||
                      "Laboratorio",
                  }));
                }}
                onCreateNew={(texto) => {
                  if (/^\d+$/i.test(texto)) {
                    setFormData((p) => ({ ...p, serial: texto }));
                  } else {
                    setFormData((p) => ({ ...p, tag: texto }));
                  }
                }}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Busca solo instrumentos de este cliente. Si no existe, podés crear uno nuevo abajo.
              </p>
            </div>

            {/* Tipo de instrumento (categoría) */}
            <div className="col-span-12 md:col-span-6">
              <div className="mb-1 flex items-center justify-between gap-3">
                <label className="block text-sm">
                  Tipo de instrumento <span className="text-rose-400">*</span>
                </label>
                {isLabManager && (
                  <button
                    type="button"
                    onClick={() => setCategoriesOpen(true)}
                    className="text-sm font-medium text-emerald-300 hover:text-emerald-200"
                    title="Crear una categoría sin abandonar este ingreso"
                  >
                    + Nueva categoría
                  </button>
                )}
              </div>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={onCategoryChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              >
                <option value="">(Seleccionar tipo…)</option>
                {categoriesForType.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                La lista muestra los tipos habilitados para la variable
                seleccionada.
              </p>
            </div>

            {/* Tag */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Tag / Identificación</label>
              <input
                type="text"
                name="tag"
                value={formData.tag}
                onChange={handleChange}
                placeholder={tagPlaceholder}
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Si no ingresás uno, se generará automáticamente.
              </p>
            </div>

            {/* Modelo */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Modelo</label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                placeholder="Ej: 232.50 / 87V / 500-196-30…"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </div>

            {/* Marca */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Marca</label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                placeholder="Ej: Mitutoyo / Fluke / WIKA…"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </div>

            {/* Serie */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Número de serie</label>
              <input
                type="text"
                name="serial"
                value={formData.serial}
                onChange={handleChange}
                placeholder="Ej: SN-123456"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </div>

            {/* Baker code */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Código de gestión (Baker)</label>
              <input
                type="text"
                name="baker_code"
                value={formData.baker_code}
                onChange={handleChange}
                placeholder="Ej: BK-000123"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Solo si el cliente usa código propio por instrumento.
              </p>
            </div>

            {/* Fecha de recepcion */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Fecha de recepcion</label>
              <input
                type="date"
                name="reception_date"
                value={formData.reception_date}
                onChange={handleChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </div>

            {/* Fecha de salida */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Fecha de salida</label>
              <input
                type="date"
                name="departure_date"
                value={formData.departure_date}
                onChange={handleChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Se completa cuando el instrumento sale del laboratorio por remito.
              </p>
              {receptionDateSupported === false && (
                <p className="text-xs text-amber-600 mt-1">
                  La base todavia no tiene esta columna; el dato no quedara guardado hasta aplicar la migracion.
                </p>
              )}
            </div>

            {/* Lugar de calibracion */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Lugar de calibracion</label>
              <select
                name="calibration_place"
                value={formData.calibration_place}
                onChange={handleChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              >
                <option value="Laboratorio">Laboratorio</option>
                <option value="In situ">In situ</option>
              </select>
            </div>
          </div>
        </section>

        {/* 2. Datos técnicos */}
        <section>
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h2 className="text-lg font-medium">2. Datos técnicos</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Este bloque queda oculto por defecto. Los técnicos pueden desplegarlo para completar los datos de calibración.
              </p>
            </div>

            <button
              type="button"
              className="px-4 py-2 rounded-md border border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
              onClick={() => setShowTechnicalData((prev) => !prev)}
            >
              {showTechnicalData ? "Ocultar datos técnicos" : "Mostrar datos técnicos"}
            </button>
          </div>

          {showTechnicalData ? (
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm mb-1">Unidad principal</label>
                <input
                  type="text"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  placeholder="Ej: bar, °C, V..."
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm mb-1">Resolución (paso)</label>
                <input
                  type="text"
                  name="resolution"
                  value={formData.resolution}
                  onChange={handleChange}
                  placeholder="Ej: 0.01"
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm mb-1">Mínimo</label>
                <input
                  type="text"
                  name="range_min"
                  value={formData.range_min}
                  onChange={handleChange}
                  placeholder="Ej: 0"
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm mb-1">Máximo</label>
                <input
                  type="text"
                  name="range_max"
                  value={formData.range_max}
                  onChange={handleChange}
                  placeholder="Ej: 100"
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm mb-1">Tolerancia (±) (opcional)</label>
                <input
                  type="text"
                  name="tolerance"
                  value={formData.tolerance}
                  onChange={handleChange}
                  placeholder="Ej: 0.1"
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm mb-1">Intervalo de recalibración (meses)</label>
                <input
                  type="text"
                  name="calibrationintervalmonths"
                  value={formData.calibrationintervalmonths}
                  onChange={handleChange}
                  placeholder="Ej: 12"
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </div>

              <div className="col-span-12">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    name="is_analog"
                    checked={!!formData.is_analog}
                    onChange={handleChange}
                  />
                  Instrumento analógico
                </label>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-100">
              Datos técnicos ocultos. Usá el botón para completarlos cuando corresponda.
            </div>
          )}
        </section>

        {/* 3. Observaciones */}
        <section>
          <h2 className="text-lg font-medium mb-3">3. Observaciones</h2>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <label className="block text-sm mb-1">
                Observaciones del instrumento (se guardan para siempre)
              </label>
              <textarea
                name="observations"
                value={formData.observations}
                onChange={handleChange}
                rows={4}
                placeholder="Ej: En caja, instrumento nuevo. Falta cable. Golpe en carcasa..."
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Estas observaciones quedan en el instrumento (no son la nota del ingreso / intake).
              </p>
            </div>
          </div>
        </section>

        {/* Acciones */}
        {errorMsg && <div className="text-sm text-red-500">{errorMsg}</div>}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 rounded-md border border-border hover:bg-muted/20"
            onClick={() => navigate(-1)}
          >
            Cancelar
          </button>

          <button
            type="submit"
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            disabled={saving}
          >
            {saving ? "Guardando…" : "Guardar instrumento"}
          </button>

          <button
            type="button"
            className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            disabled={saving}
            onClick={(e) => handleSubmit(e, { addAnother: true })}
          >
            {saving ? "Guardando…" : "Guardar y agregar otro"}
          </button>
        </div>
      </form>

      {isLabManager && (
        <InstrumentCategoriesModal
          open={categoriesOpen}
          onClose={() => setCategoriesOpen(false)}
          onCreated={handleCategoryCreated}
          defaultVariableType={formData.type}
          closeAfterCreate
        />
      )}
    </div>
  );
}

